import { Routes, Route, NavLink, Navigate } from 'react-router-dom';
import {
  Users,
  KeyRound,
  MapPin,
  Command,
  Landmark,
  CreditCard,
  Shield,
  Tablet,
  MonitorSmartphone,
  Bell,
  Target,
} from 'lucide-react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { cn } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';

// Import sub-pages
import BackOfficeUsers from './Users';
import Roles from './Roles';
import Security from './Security';
import Locations from './Locations';
import PaymentTypes from './PaymentTypes';
import Registers from './Registers';
import Kiosks from './Kiosks';
import CustomerKioskPortal from './CustomerKioskPortal';
import Alerts from './Alerts';
import SalesTargets from './SalesTargets';

interface NavItem {
  id: string;
  label: string;
  icon: React.ElementType;
  path: string;
  description?: string;
}

const navItems: NavItem[] = [
  { 
    id: 'users', 
    label: 'Users', 
    icon: Users, 
    path: '/command-center/users',
    description: 'Manage team members and permissions'
  },
  { 
    id: 'roles', 
    label: 'Roles', 
    icon: KeyRound, 
    path: '/command-center/roles',
    description: 'Configure role-based permissions'
  },
  {
    id: 'security',
    label: 'Security',
    icon: Shield,
    path: '/command-center/security',
    description: 'Authentication and access settings'
  },
  {
    id: 'locations',
    label: 'Locations',
    icon: MapPin,
    path: '/command-center/locations',
    description: 'Manage organization locations'
  },
  {
    id: 'payment-types',
    label: 'Payment Types',
    icon: CreditCard,
    path: '/command-center/payment-types',
    description: 'Configure payment methods'
  },
  {
    id: 'registers',
    label: 'Registers',
    icon: Landmark,
    path: '/command-center/registers',
    description: 'Manage cash registers per location'
  },
  {
    id: 'kiosks',
    label: 'Kiosks',
    icon: Tablet,
    path: '/command-center/kiosks',
    description: 'Configure self-service kiosk devices'
  },
  {
    id: 'customer-kiosk',
    label: 'Customer Kiosk',
    icon: MonitorSmartphone,
    path: '/command-center/customer-kiosk',
    description: 'Customer-facing kiosk portal and settings'
  },
  {
    id: 'alerts',
    label: 'Alerts',
    icon: Bell,
    path: '/command-center/alerts',
    description: 'Configure SLA alerts and notifications'
  },
  {
    id: 'sales-targets',
    label: 'Sales Targets',
    icon: Target,
    path: '/command-center/sales-targets',
    description: 'Set goals and track sales performance'
  },
];

export default function BackOffice() {
  useAuth(); // Ensure user is authenticated

  const header = (
    <div className="flex items-center justify-between gap-4">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
          <Command className="h-5 w-5 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-display font-semibold tracking-tight">
            Command Center
          </h1>
          <p className="text-sm text-muted-foreground">
            Organization settings, team management, and configuration
          </p>
        </div>
      </div>
    </div>
  );

  return (
    <DashboardLayout header={header} fullWidth>
      <div className="flex gap-6 -mt-2 min-w-0 overflow-hidden items-start">
        {/* Secondary Sidebar Navigation */}
        <aside className="w-56 shrink-0 self-start">
          <nav className="sticky top-4 space-y-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.id}
                  to={item.path}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                      'hover:bg-muted/70',
                      isActive
                        ? 'bg-primary/10 text-primary'
                        : 'text-muted-foreground hover:text-foreground'
                    )
                  }
                >
                  <Icon className="h-4 w-4" />
                  {item.label}
                </NavLink>
              );
            })}
          </nav>
        </aside>

        {/* Main Content Area */}
        <div className="flex-1 min-w-0 overflow-x-auto">
          <Routes>
            <Route path="users" element={<BackOfficeUsers />} />
            <Route path="roles" element={<Roles />} />
            <Route path="security" element={<Security />} />
            <Route path="locations" element={<Locations />} />
            <Route path="payment-types" element={<PaymentTypes />} />
            <Route path="registers" element={<Registers />} />
            <Route path="kiosks" element={<Kiosks />} />
            <Route path="customer-kiosk" element={<CustomerKioskPortal />} />
            <Route path="alerts" element={<Alerts />} />
            <Route path="sales-targets" element={<SalesTargets />} />
            <Route path="*" element={<Navigate to="/command-center/users" replace />} />
          </Routes>
        </div>
      </div>
    </DashboardLayout>
  );
}
